// src/components/charts/correlationChart/CorrelationChartContainer.jsx
import React, { useState } from "react";
import {
  Box,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  Typography,
  Button,
} from "@material-ui/core";
import CorrelationScatterChart from "./CorrelationScatterChart";
import useCorrelationData from "../../../hooks/charts/useCorrelationSeries";

const COLORS = [
  "#0072B2", // Blue
  "#D55E00", // Vermillion
  "#009E73", // Teal green
  "#CC79A7", // Reddish purple
  "#F0E442", // Yellow
  "#56B4E9", // Sky blue
];

export default function CorrelationChartContainer({
  locationids,
  p1,
  p2,
  setParams = () => {},
  height = 450,
}) {
  const [excludeOutliers, setExcludeOutliers] = useState(false);
  const aggregationMethod = "average";

  const { series, xLabel, yLabel, isLoading, isError } = useCorrelationData(
    { locationids, p1, p2 },
    aggregationMethod,
    excludeOutliers
  );

  return (
    <Box width="100%">
      {isLoading ? (
        <Box
          height={height}
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <CircularProgress />
        </Box>
      ) : isError || !series.length ? (
        <Box
          height={height}
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          No data
        </Box>
      ) : (
        <>
          <Box display="flex" p={2} mb={2} style={{ gap: "16px" }}>
            {/* your summary cards */}
            {series.map((s) => (
              <Box
                key={s.locationid}
                p={1.5}
                borderLeft={`4px solid ${COLORS[s.colorIndex % COLORS.length]}`}
                boxShadow={1}
                bgcolor="background.paper"
                role="region"
                aria-labelledby={`series-${s.name}`}
              >
                <Typography
                  id={`series-${s.name}`}
                  variant="subtitle2"
                  component="h3"
                  style={{ color: COLORS[s.colorIndex % COLORS.length] }}
                >
                  {s.name} Statistics
                </Typography>
                <Typography variant="body2">
                  y = {s.slope.toFixed(2)}x + {s.intercept.toFixed(2)}
                </Typography>
                <Typography variant="body2">
                  R²: {s.rSquared.toFixed(2)} • n: {s.n}
                </Typography>
              </Box>
            ))}
          </Box>

          <CorrelationScatterChart
            series={series}
            xLabel={xLabel}
            yLabel={yLabel}
            height={height}
            colors={COLORS}
          />

          <Box
            component="fieldset"
            mt={2}
            p={2}
            borderColor="grey.300"
            borderRadius={4}
            bgcolor="#f9f9f9"
          >
            <Typography component="legend" variant="subtitle2" gutterBottom>
              Additional Graph Filters
            </Typography>

            <Box
              display="flex"
              flexWrap="wrap"
              alignItems="flex-start"
              style={{ gap: 16 }}
            >
              <Box display="flex" flexDirection="column" minWidth={150}>
                <Typography
                  variant="caption"
                  color="textSecondary"
                  gutterBottom
                >
                  Aggregation Method
                </Typography>
                <Typography
                  variant="body2"
                  style={{ marginTop: "8px", marginLeft: "4px" }}
                >
                  Average
                </Typography>
              </Box>

              <Box display="flex" flexDirection="column">
                <Typography
                  variant="caption"
                  color="textSecondary"
                  gutterBottom
                >
                  Outlier Filtering
                </Typography>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={excludeOutliers}
                      onChange={(e) => setExcludeOutliers(e.target.checked)}
                      size="small"
                    />
                  }
                  label="Exclude outliers (±2σ)"
                />
              </Box>

              <Box display="flex" flexDirection="column" minWidth={120}>
                <Typography
                  variant="caption"
                  color="textSecondary"
                  gutterBottom
                >
                  Axes
                </Typography>
                <Button variant="outlined" onClick={setParams} size="small">
                  Swap Axes
                </Button>
              </Box>
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
}
